import { FC, useState } from 'react';
import { useRouter } from 'next/router';
import triggerTracking from '@blueprint/tracking';
import {
    FacebookColourSmall,
    TwitterColourSmall,
    LinkSmall,
    WhatsappColourSmall
} from '@blueprint/icons';
import { ButtonLink } from '@blueprint/button';
import {
    createFacebookLink,
    createTwitterLink,
    createWhatsappLink
} from './helpers';
import {
    Container,
    Note,
    ButtonContainer,
    ButtonLinkMobileOnly
} from './SocialSharing.styles';
import {
    trackEvent as trackSegmentEvent,
    EventNames,
    Taxonomy
} from '@zoopla/tracking';

const MAIN_SITE_LINK = 'https://www.zoopla.co.uk';

const note = 'Share this property with your friends and family';
const copyText = 'Copy link';
const hasCopiedText = 'Has copied';

const trackEvents = (
    sharePlatform: 'facebook' | 'twitter' | 'copy link' | 'whatsapp',
    eventCategory: string,
    taxonomy?: Taxonomy
) => {
    triggerTracking({
        event: 'GAEvent',
        eventLabel: sharePlatform,
        eventAction: 'click to share',
        eventCategory
    });

    if (taxonomy) {
        trackSegmentEvent(EventNames.SHARE_CONTENT, {
            ...taxonomy,
            sharePlatform: sharePlatform
        } as Taxonomy);
    }
};

export type Props = {
    pageTitle: string;
    eventCategory: string;
    taxonomy?: Taxonomy;
};

const SocialSharing: FC<Props> = ({ pageTitle, eventCategory, taxonomy }) => {
    const [hasCopiedToClipBoard, setHasCopiedToClipBoard] = useState(false);
    const { asPath } = useRouter();
    const url = `${MAIN_SITE_LINK}${asPath}`;
    const fbLinkUrl = createFacebookLink(url);
    const twLinkUrl = createTwitterLink(url, pageTitle);
    const whatsappLinkUrl = createWhatsappLink(url);

    const onCopyToClipboardClick = async () => {
        navigator.clipboard
            .writeText(url)
            .then(() => setHasCopiedToClipBoard(true))
            .catch(() => setHasCopiedToClipBoard(false));

        trackEvents('copy link', eventCategory, taxonomy);
    };

    return (
        <Container>
            <Note>{note}</Note>
            <ButtonContainer>
                <ButtonLink
                    variant="secondary"
                    href={fbLinkUrl}
                    onClick={() =>
                        trackEvents('facebook', eventCategory, taxonomy)
                    }
                    target="_blank"
                >
                    <FacebookColourSmall />
                    Facebook
                </ButtonLink>
                <ButtonLink
                    variant="secondary"
                    href={twLinkUrl}
                    onClick={() =>
                        trackEvents('twitter', eventCategory, taxonomy)
                    }
                    target="_blank"
                >
                    <TwitterColourSmall />
                    Twitter
                </ButtonLink>
                <ButtonLinkMobileOnly
                    variant="secondary"
                    href={whatsappLinkUrl}
                    target="_blank"
                    onClick={() =>
                        trackEvents('whatsapp', eventCategory, taxonomy)
                    }
                >
                    <WhatsappColourSmall />
                    WhatsApp
                </ButtonLinkMobileOnly>
                {'clipboard' in navigator && (
                    <ButtonLink
                        variant="secondary"
                        onClick={onCopyToClipboardClick}
                    >
                        <LinkSmall />
                        {hasCopiedToClipBoard ? hasCopiedText : copyText}
                    </ButtonLink>
                )}
            </ButtonContainer>
        </Container>
    );
};

export default SocialSharing;
